/**
 * Enregistrement de la pop-in « Configuration du projet ».
 *
 * La pop-in (`ProjectSettingsModal`) est montée à deux endroits — l'en-tête du
 * tableau kanban et la liste des projets. Sa logique d'enregistrement n'est pas
 * un simple POST : elle compare l'état saisi à l'état courant et n'envoie QUE
 * les champs qui ont bougé, parce que le champ « types de tâches activés » est
 * multivalué et que le renvoyer intégralement à chaque sauvegarde de titre
 * l'effacerait dès que la table des types n'est pas encore chargée.
 *
 * Ce module porte donc le contrat d'écriture, une seule fois : deux copies
 * divergeraient en silence (un écran enregistrerait un champ que l'autre
 * oublierait) et rien à l'écran ne le signalerait.
 *
 * La matrice de droits n'est PAS ici : elle vit dans les permissions de groupe
 * du projet et s'enregistre sur son propre endpoint (cf. `projectRights.js`).
 */

import { buildTaskTypesMap } from '../components/shared/ui/taskTypesMap';

/** Réglages du projet lus depuis la chaîne JSON `projectSettings` (jamais nulle). */
export function parseProjectSettings(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

/**
 * Diff entre l'état courant du projet et ce que la pop-in a rendu.
 *
 * @param {object} current  { name, description, picto, methodology, account, projectSettings, enabledTaskTypes, workingHours }
 * @param {object} next     ce que `ProjectSettingsModal.onSave` fournit
 * @param {Array}  taskTypes items bruts de /decoupled-api/taxonomy_term/task_type/collection
 * @returns {object} le `data` du POST — vide si rien n'a changé.
 */
/**
 * Clé d'API de l'instance de l'agent IA d'un projet : on n'en lit que l'ÉTAT,
 * `{ set, prefix }` — la clé elle-même ne revient jamais au navigateur.
 * Rejette avec le statut HTTP.
 */
export async function fetchAgentKey(projectId) {
  const res = await fetch(`/api/orbit/agent-relay/project/${projectId}/key`, { credentials: 'include' });
  if (!res.ok) throw res.status;
  return res.json();
}

/**
 * Enregistre (`{ key }`) ou retire (`{ remove: true }`) la clé. Résout avec le
 * nouvel état ; rejette avec `{ status, error }` (message du serveur, s'il y en a).
 */
export async function saveAgentKey(projectId, payload) {
  const res = await fetch(`/api/orbit/agent-relay/project/${projectId}/key`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, error: body?.error || '' };
  return body;
}

export function buildProjectSettingsPatch({ current = {}, next = {}, taskTypes = [] }) {
  const patch = {};

  const nextTitle = (next.title || '').trim();
  if (nextTitle && nextTitle !== (current.name || '')) patch.name = nextTitle;

  const nextDescription = next.description ?? '';
  if (nextDescription !== (current.description || '')) patch.description = nextDescription;

  const nextPicto = next.picto ?? '';
  if (nextPicto !== (current.picto || '')) patch.picto = nextPicto;

  // Méthodologie et client : des identifiants, comparés en CHAÎNE — le back rend
  // un nombre là où un <select> rend toujours du texte, et « 12 » ≠ 12 ferait
  // réécrire le champ à chaque enregistrement.
  const sameId = (a, b) => String(a ?? '') === String(b ?? '');
  if (next.methodology !== undefined && !sameId(next.methodology, current.methodology)) {
    patch.methodology = next.methodology;
  }
  // Clé de l'API : `account` (le mapper de la liste l'expose sous `client`).
  if (next.account !== undefined && !sameId(next.account, current.account)) {
    patch.account = next.account;
  }

  // Domaines : multivalué, en items objets `{ value }` — le tableau envoyé
  // REMPLACE la valeur. Comme pour les types activés, on ne l'écrit que s'il a
  // bougé, et jamais quand l'appelant ne le fournit pas : l'omettre ne se verrait
  // pas à l'ouverture (liste vide) mais effacerait les domaines à la sauvegarde.
  if (next.hosts !== undefined) {
    const norm = (list) => JSON.stringify((Array.isArray(list) ? list : []).map((h) => h?.value ?? h));
    if (norm(next.hosts) !== norm(current.hosts)) patch.hosts = next.hosts;
  }

  // Instance de l'agent IA : une adresse, que le serveur remet sous forme
  // canonique (ou refuse). Même règle que les domaines : jamais écrite quand
  // l'appelant ne la fournit pas.
  if (next.agentInstance !== undefined) {
    const nextInstance = (next.agentInstance || '').trim();
    if (nextInstance !== (current.agentInstance || '')) patch.agentInstance = nextInstance;
  }

  // `hide_estimation_from_client` vit dans le JSON `projectSettings` (pas de
  // champ dédié). Les clés héritées de l'ancienne matrice de droits sont
  // abandonnées à la première réécriture : le JSON cessait d'en porter une
  // seconde copie, jamais appliquée.
  //
  // `hideEstimation` est OPTIONNEL : l'appelant qui n'expose pas la case laisse
  // la valeur stockée telle quelle. L'écrire à `false` par défaut rouvrirait le
  // chiffrage aux clients au premier enregistrement du titre — un changement de
  // droits invisible, déclenché par une saisie sans rapport.
  const origSettings = parseProjectSettings(current.projectSettings);
  const { generalRights: _legacyGeneralRights, taskRights: _legacyTaskRights, ...keptSettings } = origSettings;
  const mergedSettings = { ...keptSettings };
  if (next.hideEstimation !== undefined) {
    mergedSettings.hide_estimation_from_client = !!next.hideEstimation;
  }
  if (JSON.stringify(mergedSettings) !== JSON.stringify(origSettings)) {
    patch.projectSettings = JSON.stringify(mergedSettings);
  }

  // Types activés : le champ serveur est une référence d'entité multivaluée, le
  // tableau envoyé REMPLACE la valeur. On ne l'écrit donc que si la sélection a
  // bougé ET que la table des types est chargée — sinon chaque nom résoudrait
  // vers rien et l'enregistrement viderait la sélection sans erreur.
  const origEnabled = current.enabledTaskTypes || {};
  const nextEnabled = next.enabledTaskTypes || {};
  if (JSON.stringify(nextEnabled) !== JSON.stringify(origEnabled)) {
    const { meta } = buildTaskTypesMap(taskTypes);
    if (Object.keys(meta).length > 0) {
      patch.enabledTaskTypes = Object.entries(nextEnabled)
        .filter(([, on]) => on)
        .map(([name]) => meta[name]?.tid)
        .filter((tid) => tid != null)
        .map(Number);
    }
  }

  const origHours = current.workingHours || {};
  if (JSON.stringify(next.workingHours || {}) !== JSON.stringify(origHours)) {
    patch.workingHours = next.workingHours;
  }

  return patch;
}

/**
 * POST du diff sur le projet. Renvoie la `Response` telle quelle : l'appelant
 * décide quoi faire d'un 403 (le serveur applique « edit group », un client
 * sans le droit reçoit un refus, pas un enregistrement partiel).
 */
export function postProjectSettings(projectId, patchData) {
  const url = `/decoupled-api/save?display=decoupled&entity_type=group&bundle=project&entity_id=${projectId}`;
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ submission_url: url, data: patchData, status: null }),
    credentials: 'include',
  });
}

/** Message d'échec commun aux deux écrans. */
export function projectSaveErrorMessage(status) {
  return status === 403
    ? "Vous n'avez pas les droits pour modifier ce projet."
    : `Erreur lors de l'enregistrement (${status}).`;
}

/*
 * Clé des identifiants lisibles du projet (« ORB » dans ORB-14).
 *
 * Ce n'est PAS un champ du groupe : le module orbit_ref la tient dans ses
 * propres tables (une clé abandonnée y reste un alias, pour que ses anciens
 * liens mènent toujours au ticket) et la sert sur son endpoint. Comme la matrice
 * de droits, elle s'enregistre donc à part du diff ci-dessus.
 *
 * Réponse : { enabled, prefix, history, suggestion, pattern }. Un 404 veut dire
 * que le module est désinstallé : l'appelant masque simplement le champ.
 */
const refOwnerUrl = (projectId) => `/api/orbit/ref/owner/project_task/${projectId}`;

/** GET la clé du projet. Rejette avec le statut HTTP. */
export async function fetchProjectRefKey(projectId) {
  const res = await fetch(refOwnerUrl(projectId), { credentials: 'include' });
  if (!res.ok) throw res.status;
  return res.json();
}

/**
 * POST la clé ('' la retire). Résout avec la réponse fraîche du serveur ;
 * rejette avec { status, message } — `message` est destiné à la personne qui a
 * saisi la clé (clé déjà prise, format invalide).
 */
export async function saveProjectRefKey(projectId, prefix) {
  const res = await fetch(refOwnerUrl(projectId), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prefix }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, message: body?.message || '' };
  return body;
}
